import { useLazyQuery } from "@apollo/client"
import { useCallback, useRef, useState } from "react"
import { EnvelopeAutomationStateEnum } from "@oysterhr/schema"
import { gql } from "../../../gql"
import { EnvelopePollingQueryQuery } from "../../../gql/graphql"

const ENVELOPE_IN_PROGRESS_STATES = [
  EnvelopeAutomationStateEnum.Resending,
  EnvelopeAutomationStateEnum.Sending,
  EnvelopeAutomationStateEnum.Updating,
]

const ENVELOPE_POLLING_QUERY = gql(`
  query EnvelopePollingQuery($engagementId: ID!, $isContractor: Boolean!) {
    employment(employmentId: $engagementId) @skip(if: $isContractor){
      id
      hiring {
        id
        envelopes {
          ...EnvelopeDetails
        }
        agreements {
          agreementType
          isESignEnabled
          state
        }
      }
    }

    engagement(engagementId: $engagementId) @include(if: $isContractor) {
      id
      contract {
        id
        agreements {
          id
          agreementType
          envelope {
            ...EnvelopeDetails
          }
        }
      }
    
    }
  }
`)

type ReturnType = [(cb?: (data: EnvelopePollingQueryQuery) => void) => void, boolean]

export const useAgreementsEnvelopePolling = (
  employmentId: string,
  isContractor = false,
): ReturnType => {
  const [isPolling, setIsPolling] = useState(false)
  const cbRef = useRef<(data: EnvelopePollingQueryQuery) => void>()
  const [query, { stopPolling }] = useLazyQuery(ENVELOPE_POLLING_QUERY, {
    variables: { engagementId: employmentId, isContractor },
    pollInterval: 5000,
    onCompleted: data => {
      evaluateStopPolling(data)
    },
  })

  const handleStopPolling = useCallback(
    (data: EnvelopePollingQueryQuery) => {
      stopPolling()
      setIsPolling(false)
      cbRef.current?.(data)
      cbRef.current = undefined
    },
    [stopPolling],
  )

  const evaluateStopPolling = useCallback(
    (data: EnvelopePollingQueryQuery) => {
      // EoR team member
      if (data.employment && data.employment.hiring) {
        const envelopesInProgress = data.employment.hiring?.envelopes.some(envelope =>
          ENVELOPE_IN_PROGRESS_STATES.includes(envelope.automationState),
        )

        if (!envelopesInProgress) {
          handleStopPolling(data)
        }
      }

      // Contractor
      if (data.engagement && data.engagement.contract) {
        const envelopesInProgress = data.engagement.contract.agreements.some(agreement => {
          if (!agreement.envelope) return false

          return ENVELOPE_IN_PROGRESS_STATES.includes(agreement.envelope?.automationState)
        })

        if (!envelopesInProgress) {
          handleStopPolling(data)
        }
      }
    },
    [handleStopPolling],
  )

  const startPolling = useCallback(
    (cb?: (data: EnvelopePollingQueryQuery) => void) => {
      if (!isPolling) {
        setIsPolling(true)
        cbRef.current = cb
        query()
      }
    },
    [query, isPolling],
  )

  return [startPolling, isPolling]
}
